import { useController, useFormContext } from 'react-hook-form';
import { Checkbox, SelectV2Option, Spinner } from '@cimplico/components';
import { FixedSizeList } from 'react-window';
import { Entity } from '../../../types/entity';
import { OnboardingXeroEntities } from './XeroEntitiesForm';
import { useMemo } from 'react';
import XeroEntityRow from './XeroEntityRow';

const MAX_TABLE_HEIGHT = 400;
const ROW_HEIGHT = 48;

type XeroEntitiesTableProps = {
    entities: Entity[];
    isLoading: boolean;
};

function XeroEntitiesTable({ entities, isLoading }: XeroEntitiesTableProps): JSX.Element {
    const { getValues, setValue, formState } = useFormContext<OnboardingXeroEntities>();
    const { connections, globalSelected } = getValues();
    const { field: selectField } = useController({ name: `globalSelected` });

    const TABLE_HEIGHT = connections.length * ROW_HEIGHT > MAX_TABLE_HEIGHT ? MAX_TABLE_HEIGHT : connections.length * ROW_HEIGHT;

    function handleSelect(checked: boolean): void {
        selectField.onChange(checked);
        setValue(
            'connections',
            connections.map((c) => ({ ...c, isSelected: checked }))
        );
    }

    const entitiesOptions: SelectV2Option[] = useMemo(() => {
        if (isLoading) {
            return [];
        }
        return entities.map((e) => {
            return {
                value: e.id,
                label: e.name,
                isDisabled: connections.find((v) => v.entityId === e.id) !== undefined,
            };
        });
    }, [entities, connections, isLoading]);

    return (
        <div className="flex flex-col">
            <div className="p-1.5 flex justify-between items-center">
                <Checkbox
                    checked={globalSelected}
                    disabled={formState.isSubmitting}
                    onChange={(e): void => handleSelect(e.target.checked)}
                    value={selectField.value}
                    label={`Select All (${connections.filter((c) => c.isSelected).length} / ${connections.length} selected)`}
                />
                {isLoading && (
                    <div className="flex items-center gap-2">
                        <Spinner size="xs" />
                        <p className="text-sm text-gray-600">Loading existing entities...</p>
                    </div>
                )}
            </div>
            <div className="border rounded">
                <div className="rounded-tr grid grid-cols-[45%_10%_45%] border-b py-2 text-sm font-medium text-gray-800 bg-gray-100 rounded-t px-4">
                    <p className="col-span-2 pl-4">Xero Connection</p>
                    <p className="pl-4">Existing Entity</p>
                </div>
                <FixedSizeList
                    useIsScrolling
                    height={TABLE_HEIGHT}
                    width="100%"
                    itemData={connections}
                    itemCount={connections.length}
                    itemSize={ROW_HEIGHT}
                >
                    {({ index, data, style: listStyle }): JSX.Element => {
                        return (
                            <div style={listStyle} className="border-b last:border-0">
                                <XeroEntityRow key={data[index].xeroEntity.id} entitiesOptions={entitiesOptions} index={index} />
                            </div>
                        );
                    }}
                </FixedSizeList>
            </div>
        </div>
    );
}

export default XeroEntitiesTable;
